import { parseMarkup, type ConversationView, type LocalMessage } from '@swchat/client-sdk';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ContextPanel, Icon, IconButton } from '../design';
import { useClient, useThread } from '../lib/hooks';
import { Bubble, type BubbleProps } from './Bubble';
import { ImageAlbum } from './image-album';
import { sendErrorText } from './format';

/**
 * W2.2: khung luồng trao đổi (mở ở vị trí khung ngữ cảnh; ngăn kéo / toàn màn ở bố cục hẹp): tin gốc, các phản hồi và
 * ô soạn riêng gửi tin kèm `thread_root` (chữ hoặc file đính kèm). Tin trong luồng vẫn mã hóa như tin thường.
 */
export function ThreadPanel({
  conversation,
  rootId,
  drawer,
  bubble,
  upload,
  onFiles,
  onClose,
}: {
  conversation: ConversationView;
  rootId: string;
  drawer: boolean;
  /** Props bong bóng dùng chung với dòng chính (khung luồng bỏ trả lời trích dẫn / mở luồng). */
  bubble: (m: LocalMessage) => Omit<BubbleProps, 'continued'>;
  /** File đang mã hóa + tải lên (dùng chung với ô soạn dòng chính — mỗi lúc một file). */
  upload?: { name: string; progress: number };
  /** Gửi file vào luồng này; lỗi trả về qua `onError` để hiện trong khung luồng. */
  onFiles: (files: File[], onError: (e: string) => void) => void;
  onClose: () => void;
}) {
  const client = useClient();
  const replies = useThread(conversation.id, rootId);
  const [root, setRoot] = useState<LocalMessage | null>();
  const [text, setText] = useState('');
  const [error, setError] = useState<string>();
  const end = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Tin gốc có thể nằm ngoài 200 tin đang tải của dòng chính → đọc thẳng từ store cục bộ (cập nhật khi sửa/xóa/reaction).
  useEffect(() => {
    let alive = true;
    const load = () => void client.getMessage(conversation.id, rootId).then((m) => alive && setRoot(m ?? null));
    load();
    const off = client.on('messages', (id) => id === conversation.id && load());
    return () => {
      alive = false;
      off();
    };
  }, [client, conversation.id, rootId]);

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [replies.length]);

  // Mở luồng → đặt con trỏ vào ô trả lời (đợi một khung hình để khung luồng/ngăn kéo dựng xong).
  useEffect(() => {
    if (conversation.left) return;
    const frame = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [rootId, conversation.left]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    setText('');
    setError(undefined);
    try {
      const parsed = parseMarkup(value);
      if (!parsed.text.trim()) return;
      await client.sendText(conversation.id, parsed.text, {
        threadRoot: rootId,
        entities: parsed.entities,
      });
    } catch (err) {
      setError(sendErrorText(err));
    }
  }

  const inThread = (m: LocalMessage) => ({
    ...bubble(m),
    onReply: undefined,
    onOpenThread: undefined,
    thread: undefined,
    inThread: true,
  });
  return (
    <ContextPanel title="Luồng trao đổi" onClose={onClose} testId="thread-panel" drawer={drawer}>
      <ImageAlbum messages={root ? [root, ...replies] : replies} senderName={(id) => client.getUser(id)?.display_name}>
        <div className="ws-thread">
          {root ? <Bubble {...inThread(root)} /> : root === null && <p className="ws-muted ws-small">Tin gốc không có trên thiết bị này.</p>}
          <div className="ws-thread-count ws-small ws-muted" data-testid="thread-count">
            {replies.filter((m) => !m.deleted).length} phản hồi
          </div>
          <div className="ws-thread-replies" data-testid="thread-replies">
            {replies.map((m) => (
              <Bubble key={m.id} {...inThread(m)} />
            ))}
            <div ref={end} />
          </div>
        </div>
      </ImageAlbum>
      {!conversation.left && (
        <form className="ws-thread-composer" data-testid="thread-composer" onSubmit={submit}>
          {error && (
            <div className="ws-error ws-small" role="alert">
              {error}
            </div>
          )}
          {upload && (
            <div className="ws-small ws-muted" data-testid="thread-upload-progress" role="status">
              Đang gửi {upload.name}… {Math.round(upload.progress * 100)}%
            </div>
          )}
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            data-testid="thread-attach-input"
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              e.target.value = '';
              setError(undefined);
              if (files.length) onFiles(files, setError);
            }}
          />
          <textarea
            ref={input}
            rows={2}
            value={text}
            data-testid="thread-input"
            aria-label="Trả lời trong luồng"
            placeholder="Trả lời trong luồng…"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <div className="ws-thread-actions">
            <IconButton
              label="Đính kèm file vào luồng"
              title="Đính kèm file vào luồng (mã hóa đầu cuối, tối đa 100 MB)"
              icon="clip"
              data-testid="thread-attach-button"
              disabled={!!upload}
              onClick={() => fileInput.current?.click()}
            />
            <button type="submit" className="ws-btn ws-btn-primary ws-btn-sm" data-testid="thread-send" disabled={!text.trim()}>
              <Icon name="send" size={14} />
              Gửi
            </button>
          </div>
        </form>
      )}
    </ContextPanel>
  );
}
